/// <reference types="react" />
import { createElement as h, useState, useEffect, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';

// Simple hash router
function useHashRoute() {
  const [hash, setHash] = useState(window.location.hash || '#/');
  useEffect(() => {
    const onHash = () => setHash(window.location.hash || '#/');
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  return hash;
}

// Lazy-loaded components
const AssociationGraph = lazy(() => import('./components/AssociationGraph'));
const SignificanceChart = lazy(() => import('./components/SignificanceChart'));

// ── Components ──

function Dashboard() {
  return h('div', { style: { padding: 24 } },
    h('h1', { style: { fontSize: 24, fontWeight: 700, marginBottom: 8 } }, 'BundleGenius Dashboard'),
    h('p', { style: { color: '#666', marginBottom: 24 } }, 'AI-powered bundle creation & optimization'),
    h('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 } },
      h(StatCard, { title: 'Total Bundles', value: '0', color: '#667eea' }),
      h(StatCard, { title: 'Active Tests', value: '0', color: '#f5576c' }),
      h(StatCard, { title: 'Avg AOV Lift', value: '--', color: '#38f9d7' }),
      h(StatCard, { title: 'Clearance Items', value: '0', color: '#f093fb' }),
    ),
    h('div', { style: { display: 'flex', gap: 12, flexWrap: 'wrap' } },
      h(NavButton, { href: '#/analysis', icon: '🔍', label: 'Run Analysis' }),
      h(NavButton, { href: '#/associations', icon: '📊', label: 'Association Graph' }),
      h(NavButton, { href: '#/candidates', icon: '🤖', label: 'AI Candidates' }),
      h(NavButton, { href: '#/tests', icon: '🧪', label: 'A/B Tests' }),
      h(NavButton, { href: '#/clearance', icon: '📦', label: 'Clearance' }),
      h(NavButton, { href: '#/settings', icon: '⚙️', label: 'Settings' }),
    ),
  );
}

function StatCard({ title, value, color }: { title: string; value: string; color: string }) {
  return h('div', {
    style: {
      background: 'white', borderRadius: 12, padding: 20,
      borderLeft: `4px solid ${color}`, boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    },
  },
    h('div', { style: { fontSize: 13, color: '#888', marginBottom: 4 } }, title),
    h('div', { style: { fontSize: 28, fontWeight: 700 } }, value),
  );
}

function NavButton({ href, icon, label }: { href: string; icon: string; label: string }) {
  return h('a', {
    href,
    style: {
      display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 20px',
      background: 'white', borderRadius: 8, textDecoration: 'none', color: '#333',
      fontSize: 14, fontWeight: 500, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      transition: 'box-shadow 0.2s',
    },
  },
    h('span', null, icon),
    h('span', null, label),
  );
}

function Analysis() {
  const [status, setStatus] = useState<string>('idle');
  const [results, setResults] = useState<any>(null);

  const runAnalysis = async () => {
    setStatus('running');
    try {
      const res = await fetch('/api/v1/assoc/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ days_history: 90 }),
      });
      const data = await res.json();
      setResults(data);
      setStatus('completed');
    } catch (e) {
      setStatus('error');
    }
  };

  return h('div', { style: { padding: 24 } },
    h('h2', { style: { fontSize: 20, marginBottom: 16 } }, 'Association Analysis'),
    h('div', { style: { display: 'flex', gap: 12, marginBottom: 16 } },
      h('button', {
        onClick: runAnalysis,
        disabled: status === 'running',
        style: { padding: '10px 24px', background: '#667eea', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 15, fontWeight: 600 },
      }, status === 'running' ? 'Analyzing...' : 'Start Analysis'),
      h('button', {
        onClick: async () => {
          try {
            const r = await fetch('/api/v1/admin/seed', { method: 'POST' });
            const d = await r.json();
            alert(`Seed done! ${d.total_orders} orders, ${d.total_pairs} pairs`);
          } catch(e) { alert('Seed failed'); }
        },
        style: { padding: '10px 24px', background: '#38f9d7', color: '#1a1a2e', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 15, fontWeight: 600 },
      }, '🌱 Seed Test Data'),
    ),
    status === 'completed' && results && h('div', { style: { marginTop: 20 } },
      h('p', null, `Found ${results.total_pairs ?? 0} association pairs from ${results.total_orders ?? 0} orders.`),
      results.top_pairs && results.top_pairs.length > 0 ? h('table', { style: { width: '100%', borderCollapse: 'collapse', marginTop: 12 } },
        h('thead', null, h('tr', null,
          h('th', null, 'Product A'), h('th', null, 'Product B'), h('th', null, 'Lift'), h('th', null, 'Co-purchases'),
        )),
        h('tbody', null, results.top_pairs.map((p: any) =>
          h('tr', { key: p.product_a + p.product_b },
            h('td', null, p.product_a), h('td', null, p.product_b),
            h('td', null, p.lift), h('td', null, p.co_purchase_count),
          )
        )),
      ) : h('p', { style: { color: '#888', marginTop: 8 } }, 'No significant associations found. Try with more order data or lower thresholds.'),
    ),
  );
}

function Candidates() {
  return h('div', { style: { padding: 24 } },
    h('h2', null, 'AI Bundle Candidates'),
    h('p', { style: { color: '#888' } }, 'Generate AI-powered bundle recommendations based on association data.'),
  );
}

function Associations() {
  return h('div', { style: { padding: 24 } },
    h('h2', null, 'Association Graph'),
    h(Suspense, { fallback: h('div', null, 'Loading graph...') },
      h(AssociationGraph),
    ),
  );
}

function Tests() {
  return h('div', { style: { padding: 24 } },
    h('h2', null, 'A/B Tests'),
    h('p', { style: { color: '#888' } }, 'Monitor ongoing bundle tests and statistical significance.'),
  );
}

function Clearance() {
  return h('div', { style: { padding: 24 } },
    h('h2', null, 'Clearance Opportunities'),
    h('p', { style: { color: '#888' } }, 'Identify overstocked products and pair with best-selling items.'),
  );
}

function Settings() {
  return h('div', { style: { padding: 24 } },
    h('h2', null, 'Strategy Settings'),
    h('p', { style: { color: '#888' } }, 'Configure bundle optimization weights and constraints.'),
  );
}

// ── App Router ──

function App() {
  const hash = useHashRoute();
  const page = hash.replace('#', '') || '/';

  return h('div', { style: { maxWidth: 1200, margin: '0 auto' } },
    h(NavBar),
    page === '/' || page === '/dashboard' ? h(Dashboard) :
    page === '/analysis' ? h(Analysis) :
    page === '/associations' ? h(Associations) :
    page === '/candidates' ? h(Candidates) :
    page === '/tests' ? h(Tests) :
    page === '/clearance' ? h(Clearance) :
    page === '/settings' ? h(Settings) :
    h(Dashboard),
  );
}

function NavBar() {
  const links = [
    { href: '#/', label: 'Dashboard' },
    { href: '#/analysis', label: 'Analysis' },
    { href: '#/associations', label: 'Graph' },
    { href: '#/candidates', label: 'Candidates' },
    { href: '#/tests', label: 'Tests' },
    { href: '#/clearance', label: 'Clearance' },
    { href: '#/settings', label: 'Settings' },
  ];

  return h('nav', {
    style: {
      display: 'flex', gap: 4, padding: '12px 24px', background: '#1a1a2e',
      alignItems: 'center', flexWrap: 'wrap',
    },
  },
    h('span', { style: { color: 'white', fontWeight: 700, fontSize: 16, marginRight: 24 } }, '🎁 BundleGenius'),
    ...links.map(l => h('a', {
      href: l.href,
      style: {
        color: '#aab', textDecoration: 'none', padding: '6px 12px',
        borderRadius: 6, fontSize: 14, fontWeight: 500,
        background: window.location.hash === l.href ? 'rgba(255,255,255,0.1)' : 'transparent',
      },
    }, l.label)),
  );
}

// ── Entry ──
createRoot(document.getElementById('root')!).render(h(App));
